<template>
  <div>
    <slot v-if="somentePendentes" :pendencias="pendenciasDisponiveis"></slot>
    <div
      v-else-if="indicadoresDisponiveis.length"
      class="mais-indicadores-tabs"
    >
      <div v-if="title != ''" class="mais-indicadores-header">
        <h4 class="mais-indicadores-title">{{ title }}</h4>
      </div>

      <div class="mais-indicadores-grid">
        <button
          v-for="item in indicadoresDisponiveis"
          :key="item.value"
          type="button"
          class="mais-indicadores-card"
          :class="[
            { active: internalValue === item.value },
            getClasseIndicador(item),
          ]"
          @click="selectTab(item.value)"
        >
          <div
            v-if="internalValue === item.value"
            class="mais-indicadores-card-arrow"
            :class="getClasseIndicador(item)"
          ></div>

          <div class="mais-indicadores-icon">
            <component
              v-if="item.iconType === 'component'"
              :is="item.iconComponent"
              class="mais-indicadores-icon-component"
            />

            <img
              v-else-if="item.iconType === 'image'"
              :src="item.iconImage"
              :alt="item.label"
              class="mais-indicadores-icon-image"
            />

            <i
              v-else-if="item.iconType === 'class'"
              :class="item.iconClass"
              class="mais-indicadores-icon-class"
            ></i>
          </div>

          <div class="mais-indicadores-label">
            {{ item.label }}
          </div>
        </button>
      </div>

      <div class="mais-indicadores-content">
        <!-- ALERTS -->
        <div v-if="activeBuscaAtivaItems.length">
          <div
            v-for="item in activeBuscaAtivaItems"
            :key="item.key"
            class="mais-indicadores-alert"
            :class="getClasseAlert(item)"
          >
            <!-- <div class="mais-indicadores-alert-icon">
          <i class="icofont-info-circle"></i>
        </div> -->

            <div class="mais-indicadores-alert-text">
              {{ item.text }}
            </div>
          </div>
        </div>

        <!-- 100% -->
        <div v-else class="mais-indicadores-alert completo">
          <!-- <div class="mais-indicadores-alert-icon">
          <i class="icofont-check-circled"></i>
        </div> -->

          <div class="mais-indicadores-alert-text">
            Pessoa com atendimento em dia.
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import utils from '@/mixins/utils'
import { indicadoresConfig } from './constants/index.js'

export default {
  name: 'MaisIndicadoresTabs',
  components: {},
  props: {
    value: {
      type: [String, Number],
      default: null,
    },
    title: {
      type: String,
      default: 'MAIS INDICADORES',
    },
    cidadao: {
      type: Object,
      required: true,
    },
    indicadorAtual: {
      type: String,
      default: '',
    },
    somentePendentes: {
      type: Boolean,
      default: false,
    },
  },
  mixins: [utils],
  data() {
    return {
      internalValue: this.value,
    }
  },
  computed: {
    /**
     * Retorna o mapa de configuração dos indicadores.
     *
     * @returns {Object} Configuração centralizada dos indicadores
     */
    mapaIndicadores() {
      return indicadoresConfig
    },

    /**
     * Converte a string JSON de indicadores em objeto.
     *
     * @returns {Object} Objeto com dados da indicadores
     */
    indicadoresParseados() {
      try {
        const indicadores = this.cidadao?.indicadores
        if (!indicadores) return {}

        return typeof indicadores === 'string'
          ? JSON.parse(indicadores)
          : indicadores
      } catch (error) {
        return {}
      }
    },

    /**
     * Converte a string JSON de busca ativa em objeto.
     *
     * @returns {Object} Objeto com dados da busca ativa
     */
    buscaAtivaParseada() {
      try {
        const buscaAtiva = this.cidadao?.busca_ativa
        if (!buscaAtiva) return {}

        return typeof buscaAtiva === 'string'
          ? JSON.parse(buscaAtiva)
          : buscaAtiva
      } catch (error) {
        return {}
      }
    },

    /**
     * Retorna os indicadores disponíveis para exibição.
     *
     * Regras:
     * - Aceita qualquer indicador vindo do backend
     * - Remove o indicador atual da página
     * - Exibe apenas indicadores com valor < 100 (pendentes)
     * - Usa configuração específica quando existir
     *
     * @returns {Array} Lista de indicadores formatados
     */
    indicadoresDisponiveis() {
      return (
        Object.keys(this.indicadoresParseados)
          .filter((key) => key !== this.indicadorAtual) // diferente do card atual
          // .filter((key) => Number(this.indicadoresParseados[key]) < 100) // apenas os < que 100
          .sort()
          .map((key) => {
            const config = this.mapaIndicadores[key]

            return {
              value: key,
              quantidade: this.indicadoresParseados[key],
              ...(config || {
                label: key.toUpperCase(),
                iconType: 'class',
                iconClass: 'icofont-question-circle',
                buscaAtiva: {},
              }),
            }
          })
      )
    },

    /**
     * Retorna o indicador atualmente selecionado.
     *
     * A busca é feita na lista de indicadores disponíveis
     * com base no valor selecionado (internalValue).
     *
     * @returns {Object|null} Indicador ativo ou null caso não encontrado
     */
    activeItem() {
      return (
        this.indicadoresDisponiveis.find(
          (item) => item.value === this.internalValue
        ) || null
      )
    },

    /**
     * Retorna os itens de busca ativa do indicador atualmente selecionado.
     *
     * A lógica considera:
     * - Prefixo do indicador (ex: c3)
     * - Chaves da busca ativa (ex: c3a, c3b...)
     * - Texto estático ou dinâmico com base no cidadão
     *
     * @returns {Array} Lista de itens de busca ativa formatados
     */
    activeBuscaAtivaItems() {
      if (!this.activeItem) return []

      const prefix = this.activeItem.value
      const definicoes = this.activeItem.buscaAtiva || {}

      return Object.keys(this.buscaAtivaParseada)
        .filter((key) => key.startsWith(prefix))
        .sort()
        .map((key) => {
          const sufixo = key.replace(prefix, '')
          const definicao = definicoes[sufixo]

          return {
            key,
            ordem: sufixo,
            valor: this.buscaAtivaParseada[key],
            text: this.getTextoBuscaAtiva(definicao, key),
          }
        })
        .filter((item) => Number(item.valor) === 0)
    },

    /**
     * Verifica se o indicador ativo está 100% acompanhado.
     *
     * @returns {Boolean}
     */
    isIndicadorCompleto() {
      if (!this.activeItem) return false

      return !this.hasBuscaAtivaPendente(this.activeItem.value)
    },

    /**
     * Retorna todas as pendências dos indicadores disponíveis.
     *
     * Considera os itens da busca ativa com valor igual a zero,
     * utilizando as configurações existentes para gerar os textos.
     *
     * @returns {Array<Object>} Lista de pendências dos indicadores.
     */
    pendenciasDisponiveis() {
      return this.indicadoresDisponiveis.flatMap((indicador) => {
        const prefixo = indicador.value
        const definicoes = indicador.buscaAtiva || {}

        return Object.keys(this.buscaAtivaParseada)
          .filter(
            (key) =>
              key.startsWith(prefixo) &&
              Number(this.buscaAtivaParseada[key]) === 0
          )
          .sort()
          .map((key) => {
            const sufixo = key.replace(prefixo, '')
            const definicao = definicoes[sufixo]

            return {
              key,
              indicador: prefixo,
              text: this.getTextoBuscaAtiva(definicao, key),
            }
          })
      })
    },
  },
  watch: {
    value: {
      immediate: true,
      handler(newValue) {
        this.internalValue = newValue || this.getPrimeiroIndicadorAtivo()
      },
    },
    indicadoresDisponiveis: {
      immediate: true,
      handler(newItems) {
        if (!newItems.length) {
          this.internalValue = null
          return
        }

        const exists = newItems.some(
          (item) => item.value === this.internalValue
        )

        if (!exists) {
          this.internalValue = this.getPrimeiroIndicadorAtivo()
          this.$emit('input', this.internalValue)
          this.$emit('change', this.internalValue)
        }
      },
      deep: true,
    },
    cidadao: {
      deep: true,
      handler() {
        this.setPrimeiroIndicadorDisponivel()
      },
    },
  },
  methods: {
    /**
     * Define o indicador ativo (tab selecionada).
     *
     * @param {String} value Identificador do indicador (ex: c3)
     */
    selectTab(value) {
      this.internalValue = value
      this.$emit('input', value)
      this.$emit('change', value)
    },

    /**
     * Verifica se o indicador possui pelo menos um item pendente na busca ativa.
     *
     * Regra:
     * - Considera pendente quando o valor do item é igual a 0
     *
     * @param {String} indicadorKey Identificador do indicador (ex: b1, c3)
     * @returns {Boolean}
     */
    hasBuscaAtivaPendente(indicadorKey) {
      return Object.keys(this.buscaAtivaParseada).some((key) => {
        return (
          key.startsWith(indicadorKey) &&
          Number(this.buscaAtivaParseada[key]) === 0
        )
      })
    },

    /**
     * Retorna o primeiro indicador com pendência na busca ativa.
     *
     * Caso nenhum tenha pendência, retorna o primeiro indicador disponível.
     *
     * @returns {String|null} Identificador do indicador inicial
     */
    getPrimeiroIndicadorAtivo() {
      if (!this.indicadoresDisponiveis.length) return null

      const primeiroComPendencia = this.indicadoresDisponiveis.find((item) =>
        this.hasBuscaAtivaPendente(item.value)
      )

      return primeiroComPendencia
        ? primeiroComPendencia.value
        : this.indicadoresDisponiveis[0].value
    },

    /**
     * Retorna a classe visual do card com base na busca ativa.
     *
     * @param {Object} item
     * @returns {String}
     */
    getClasseIndicador(item) {
      return this.hasBuscaAtivaPendente(item.value)
        ? 'indicador-incompleto'
        : 'indicador-completo'
    },

    /**
     * Define a tab inicial com base nas pendências da busca ativa.
     *
     * @returns {void}
     */
    setPrimeiroIndicadorDisponivel() {
      this.internalValue = this.getPrimeiroIndicadorAtivo()

      this.$emit('input', this.internalValue)
      this.$emit('change', this.internalValue)
    },

    /**
     * Retorna o texto da busca ativa.
     *
     * Aceita:
     * - string estática
     * - função que recebe contexto do cidadão
     *
     * @param {String|Function|undefined} definicao
     * @param {String} key
     * @returns {String}
     */
    getTextoBuscaAtiva(definicao, key) {
      if (typeof definicao === 'function') {
        return definicao({
          cidadao: this.cidadao,
          formatDate: this.formatDate,
        })
      }

      if (typeof definicao === 'string') {
        return definicao
      }

      return `Item ${key}`
    },

    /**
     * Retorna a classe visual do alert.
     *
     * Regra:
     * - Pendência → vermelho
     * - Completo → verde
     *
     * @returns {String}
     */
    getClasseAlert() {
      return this.isIndicadorCompleto ? 'alert-verde' : 'alert-vermelho'
    },
  },
}
</script>

<style scoped lang="scss">
.mais-indicadores-tabs {
  margin: 20px 0;
  padding-top: 20px;

  .mais-indicadores-header {
    display: flex;
    align-items: center;
    justify-content: space-between;
    margin-bottom: 18px;
    border-bottom: 1px solid #e5e7eb;
    padding-bottom: 10px;
  }

  .mais-indicadores-title {
    margin: 0;
    font-size: 14px;
    font-weight: 700;
    letter-spacing: 0.08em;
    color: #5b6575;
  }

  .mais-indicadores-grid {
    display: grid;
    grid-template-columns: repeat(5, minmax(0, 1fr));
    gap: 18px;
  }

  .mais-indicadores-card {
    position: relative;
    border: 1px solid #d9e4f5;
    border-radius: 18px;
    background: #fff;
    min-height: 158px;
    padding: 22px 16px;

    display: flex;
    align-items: center;
    justify-content: center;
    flex-direction: column;

    transition: all 0.2s ease;
    box-shadow: 0 4px 10px rgba(24, 72, 128, 0.04);

    &:hover {
      border-color: #b9cdea;
    }

    &.active {
      border: 2px solid #4e8cff;
      box-shadow: 0 0 0 3px rgba(78, 140, 255, 0.08);
    }

    &.indicador-incompleto {
      border: 2px solid #ef4444;
    }

    &.indicador-completo {
      border: 2px solid #22c55e;
    }

    .mais-indicadores-card-arrow {
      position: absolute;
      bottom: -10px;
      left: 50%;
      transform: translateX(-50%);
      width: 0;
      height: 0;

      border-left: 10px solid transparent;
      border-right: 10px solid transparent;
      border-top: 10px solid #2563eb;

      &.indicador-incompleto {
        border-top-color: #ef4444;
      }

      &.indicador-completo {
        border-top-color: #22c55e;
      }
    }
  }

  .mais-indicadores-icon {
    width: 70px;
    height: 70px;
    display: flex;
    align-items: center;
    justify-content: center;
    margin-bottom: 14px;

    &-component {
      width: 56px;
      height: 56px;
      display: block;
    }

    &-image {
      max-width: 56px;
      max-height: 56px;
      object-fit: contain;
    }

    &-class {
      font-size: 52px;
      line-height: 1;
    }
  }

  .mais-indicadores-label {
    font-size: 16px;
    font-weight: 500;
    color: #111827;
    text-align: center;
    line-height: 1.35;
  }

  .mais-indicadores-content {
    display: flex;
    flex-direction: column;
    gap: 12px;
    margin: 20px 0;
  }

  .mais-indicadores-alert {
    display: flex;
    align-items: flex-start;
    gap: 12px;
    border-radius: 8px;
    padding: 14px 16px;
    font-size: 16px;
    line-height: 1.45;
    margin-bottom: 10px;

    &.alert-vermelho {
      background: #fff1f1;
      border: 1px solid #fca5a5;
      color: #b91c1c;
    }

    &.alert-verde,
    &.completo {
      background: #ecfdf5;
      border: 1px solid #86efac;
      color: #166534;
    }

    &-icon {
      font-size: 20px;
      line-height: 1;
      margin-top: 2px;
    }
  }

  .mais-indicadores-slot {
    margin-top: 12px;
  }
}

@media (max-width: 1200px) {
  .mais-indicadores-tabs .mais-indicadores-grid {
    grid-template-columns: repeat(3, minmax(0, 1fr));
  }
}

@media (max-width: 991px) {
  .mais-indicadores-tabs .mais-indicadores-grid {
    grid-template-columns: repeat(2, minmax(0, 1fr));
  }
}

@media (max-width: 576px) {
  .mais-indicadores-tabs .mais-indicadores-grid {
    grid-template-columns: 1fr;
  }
}
</style>
